import React, { useState } from 'react';
import { css } from '@emotion/css';
import FallbackMessageContainer from "../../state/containers/FallbackMessageContainer";
import PageContainer from '../../components/structure/PageContainer';
import FallbackMessageModel from '../../models/features/api/Fallback/FallbackMessageModel';
import TitleTextField from '../../components/forms/TitleTextField';
import FollowUpWithRepromptForm, { FollowUpWithRepromptFormData, FollowUpWithRepromptFormProps } from '../followUpForm';
import ConversationItemFormProps from '../../models/features/ConversationItemFormProps';
import ConversationFlow from '../conversationFlow';
import Loader from '../../components/general/Loader';
import { FallbackFeatureTypeId } from '../../constants/featureTypeIds';
import IResult from '../../models/result/IResult';
import ContentLanguageMultiSelector from '../../components/forms/ContentLanguageMultiSelector';
import ContentPagePanel from '../contentPage';
import RelatedContentMenu from '../relatedContentMenu';
import ScrollablePanelBody from '../../components/structure/ScrollablePanelBody';
import PanelTitle from '../contentPage/components/PanelTitle';
import ContentCollapsingSection from '../../components/structure/ContentCollapsingSection';
import ContentSectionTitle from '../contentPage/components/ContentSectionTitle';
import { getFeatureTypeIcon } from '../../models/extensions';
import { TreeItem } from '../../components/structure/NestedTree/NestedTree';
import PageError from '../../components/general/PageError';
import ConversationItemActionButtonToolbar from './components/ConversationItemActionButtonToolbar';
import TemplateLockedBanner from '../../components/general/TemplateLockedBanner';
import ConversationItemFieldSet from '../../components/forms/ConversationItemFieldSet';
import ContentInfoOptions from '../../components/structure/ContentInfoOptions';
import { WebhookChainingType } from '../../models/webhooks/api/WebhookChainingType';
import FallbackMessageResponseModel from '../../models/features/api/Fallback/FallbackMessageResponseModel';
import { ContentItemPageState } from './BaseConversationItemForm';
import ResponsesManager from '../responsesManager';
import ResponseGroup from '../../models/features/ResponseGroup';
import SecondaryLoader from '../../components/general/SecondaryLoader';
import SelectField from '../../components/forms/SelectField';
import { DirtyFormPrompt } from './components/DirtyFormPrompt';
import SensitiveDataOptions from '../contentPage/components/SensitiveDataOptions';
import DisableForFeatureFlagOptions from '../contentPage/components/DisableForFeatureFlagOptions';
import styled from '@emotion/styled';
import ContentItemTestCaseViewer from './ContentItemTestCaseViewer';

const contentInfoIcon = require('../../content/images/content-tree-icons/content-level/task-list-pin.svg');
const continueIcon = require('../../content/images/await.svg');

export interface EditFallbackMessageFormData extends FollowUpWithRepromptFormData {
    title?: string
    categoryId?: string
    responses: FallbackMessageResponseModel[]
    webhookChainingType?: WebhookChainingType
}

export interface EditFallbackMessageFormProps extends FollowUpWithRepromptFormProps<FallbackMessageModel>, ConversationItemFormProps<FallbackMessageModel> {
    stateContainer: FallbackMessageContainer
    fallbackMessage?: FallbackMessageModel
}

class FallbackMessageForm extends FollowUpWithRepromptForm<FallbackMessageModel, EditFallbackMessageFormProps, EditFallbackMessageFormData, ContentItemPageState> {

    componentDidUpdate(
        prevProps: EditFallbackMessageFormProps,
        prevState: ContentItemPageState
    ) {
        super.componentDidUpdate(prevProps, prevState)
    }

    handleResponseIdFromSim = (id: string) => {
        this.setState({ simulatedResponseId: id })
    }

    componentDidMount() {
        this.props.stateContainer.getFallbackCategories();
    }
    handleAsyncSubmit(onSuccess: (result: IResult<FallbackMessageModel>) => void) {
        if (this.manualValidate())
            return this.props.onSubmit(this.props.stateContainer.mapFormDataToModel(this.props.fallbackMessage, this.props.values), onSuccess);
    }

    handleDiscardChanges() {
        this.handleFinish();
    }

    handleCopy() {
        this.handleAsyncSubmit(() => {
            this.props.stateContainer.copy(this.props.fallbackMessage.id).then(r2 => {
                if (r2.resultType == "Ok") {
                    this.props.history.push(`/v/${r2.data.applicationFeatureId}/fallbackmessages/${r2.data.id}`);
                }
            });
        });
    }

    handleDelete() {
        const confirmed = confirm("Are you sure you want to delete this?");
        if (confirmed) {
            this.props.stateContainer.delete(this.props.fallbackMessage.id).then(() => {
                this.handleFinish();
            });
        }
    }

    handleCategoryChange(value) {
        const finalValue = value === null ? null : value.value;
        this.props.setFieldValue("categoryId", finalValue);
    }

    handleCategoryBlur(blur) {
        this.props.setFieldTouched("categoryId", blur);
    }

    handleSsmlUpdate(ssml: string) {
        this.props.setFieldValue('content', ssml);
    }

    getMenuTreeRoots() {
        var roots: TreeItem[] = [this.getContentInfoTreeItem(),
        ...this.getResponsesTreeItems(this.props.values.responses)]

        return roots;
    }

    handleContentItemOptionSelected(option: string, event: any) {
        event.stopPropagation();
        if (option == "Manage Webhooks") {
            this.showWebhookInstanceManager();
        }
        else if (option == "Duplicate") {
            this.handleCopy();
        }
        else if (option == "Delete") {
            this.handleDelete();
        }
    }

    getContentItem() {
        return this.props.fallbackMessage;
    }

    handleResponsesChange(responseGroups: ResponseGroup[]) {
        this.props.setFieldValue("responses", this.getResponsesFromGroups(responseGroups));
    }

    render() {
        const props = { ...this.props };
        const isLoading = props.stateContainer.state.isSaving;
        const isDisabled = isLoading || this.props.isTemplateOwned;
        const categories = props.stateContainer.state.categories;
        const responseGroups = this.getResponseGroups(this.props.values.responses);
        const { simulatedResponseId } = this.state;
        if (this.props.responseStateContainer.state.isLoading || !this.props.application) {
            return <Loader />
        }
        return (
            <>
                <form onSubmit={props.handleSubmit}>
                    <DirtyFormPrompt allowPrompt={props.status?.submitting != true} />
                    <ContentPagePanel
                        relatedContentRender={() => (<RelatedContentMenu
                            parentContentId={this.props.previousContentId}
                            conversationItem={{ ...this.props.fallbackMessage, featureTypeId: FallbackFeatureTypeId }}
                            history={this.props.history}
                            featureTypeId={FallbackFeatureTypeId}
                            followUp={responseGroups?.[0]?.firstResponse?.followUp}
                            contentId={this.props.fallbackMessage?.id}
                            onClose={this.props.onToggleNavigation}
                            onSave={this.handleAsyncSubmit.bind(this)} />)
                        }
                        treeRender={this.renderTree.bind(this)}
                        formPanelRender={() => (
                            <PageContainer fill>
                                <ConversationItemActionButtonToolbar
                                    errors={Object.values(props.errors)}
                                    validateForm={props.validateForm}
                                    publishDisabled={this.props.assistantApplication == null}
                                    saveDisabled={Object.keys(props.errors).length > 0}
                                    contentItem={props.fallbackMessage}
                                    isLoading={isLoading}
                                    handleDiscardChanges={this.handleDiscardChanges.bind(this)}
                                    handlePublishing={this.handleSaveAndPublish.bind(this)}
                                    submitForm={props.submitForm}
                                />
                                {props.fallbackMessage?.createdFromId && <TemplateLockedBanner isLoading={props.stateContainer.state.isTogglingSync} isLinked={props.isTemplateOwned} onRelink={this.handleToggleRelinkModal.bind(this)} onUnlink={() => this.props.onToggleSync(this.props.fallbackMessage.id, true)} />}

                                <ConversationItemFieldSet disabled={isDisabled} withBanner={props.fallbackMessage?.createdFromId != null}>
                                    <ScrollablePanelBody className="scrollable-body">
                                        <PanelTitle title="Content Editing"
                                            detail="Here’s where you can fill in the info about this content and determine the course of the conversation by filling in the relevant details.">
                                        </PanelTitle>
                                        <ContentCollapsingSection title="Content Info" id="contentInfo" icon={contentInfoIcon} titleRightRowComponent={
                                            <ContentInfoOptions webhookCount={this.props.stateContainer.state.webhookUpdates?.webhookInstances?.length ?? this.props.stateContainer.state.attachedWebhooks?.length ?? 0}
                                                options={this.getOptions(this.props.fallbackMessage != null)}
                                                optionSelected={this.handleContentItemOptionSelected.bind(this)}
                                                manageWebhooks={this.showWebhookInstanceManager.bind(this)}
                                                showSensitiveConversation={this.state.showSensitiveConversation}
                                                toggleSensitiveConversation={() => { this.toggleSensitiveConversation(); }}
                                                showDisabledForFeatures={this.state.showDisabledForFeatureFlags}
                                                toggleDisabledForFeatures={() => { this.toggleDisableForFeatureFlags(); }}
                                                flagsSelected={props.values.disabledForFeatureFlagIds?.length}
                                            />
                                        }>
                                            {this.state.showSensitiveConversation &&
                                                <SensitiveDataOptions
                                                    selectedOption={props.values.sensitiveConversationChange ?? "None"}
                                                    onChange={(value) => { props.setFieldValue("sensitiveConversationChange", value) }}
                                                />
                                            }
                                            {this.state.showDisabledForFeatureFlags &&
                                                <DisableForFeatureFlagOptions
                                                    appContainer={this.props.appContainer}
                                                    onChange={(value) => { props.setFieldValue("disabledForFeatureFlagIds", value) }}
                                                    selectedFeatureFlags={props.values.disabledForFeatureFlagIds}
                                                />
                                            }
                                            <ContentSectionTitle title="Fallback Message Conversation" icon={getFeatureTypeIcon(FallbackFeatureTypeId, true)} />
                                            <TitleTextField id="contentTitle" disabled={isDisabled} name="title" placeholder="For display only" value={props.values.title} label="Title" onChange={props.handleChange} onBlur={props.handleBlur} />
                                            <ContentLanguageMultiSelector id="contentLanguage" disabled={isDisabled} label="Language / Region" languages={props.application.languages} selectedLanguageIds={props.values.languages.map(l => l.id)} onChange={this.handleLanguagesChange.bind(this)} />
                                            {this.renderOptions(false, isDisabled)}
                                            {categories.length == 0 || props.stateContainer.state.isLoadingCategories ?
                                                <SecondaryLoader /> :
                                                <SelectField name="categoryId"
                                                    className={selectFieldStyle}
                                                    label="Fallback Category"
                                                    onChange={this.handleCategoryChange.bind(this)}
                                                    onBlur={this.handleCategoryBlur.bind(this)}
                                                    value={props.values.categoryId}
                                                    disabled={isDisabled}
                                                    options={categories.map(o => ({ value: o.id, label: o.name }))} />
                                            }
                                        </ContentCollapsingSection>
                                        <ResponsesManager applicationId={this.props.application.id}
                                            applicationFeatureId={this.props.applicationFeatureId}
                                            languages={this.props.values.languages}
                                            responseGroups={responseGroups}
                                            getConditionsKey={(c) => this.getConditionsKey(c)}
                                            showError={this.showError.bind(this)}
                                            isDisabled={isDisabled}
                                            onChange={this.handleResponsesChange.bind(this)}
                                            onBlur={this.props.handleBlur}
                                            emptyFollowUpOptionText={"Default - await further user input"}
                                            emptyFollowUpOptionIcon={continueIcon}
                                            showEndSessionOption={true}
                                            simulatedResponseId={simulatedResponseId}
                                            responseIdToNamesRef={this.responseIdToNamesRef}
                                            contentItemTitle={this.props.values.title} />
                                    </ScrollablePanelBody>
                                </ConversationItemFieldSet>
                                <PageError errors={this.props?.stateContainer?.state?.errors ?? []} onClear={this.handleClearError.bind(this)} />
                            </PageContainer>
                        )}

                        testCasesRender={() => (
                            <ContentItemTestCaseViewer application={this.props.application} contentItemId={this.props.fallbackMessage?.id} getResponseId={this.handleResponseIdFromSim} responseIdToNamesRef={this.responseIdToNamesRef} />
                        )}>
                    </ContentPagePanel>
                </form >
                {this.renderRelinkSyncWarning()}
                {this.renderWebhookInstanceManager(this.props.application.id, this.props.fallbackMessage?.id, FallbackFeatureTypeId, this.props.values.webhookChainingType)}
            </>
        )
    }
}

const selectFieldStyle = css`
    margin-top: 24px;
`

const StyledContainer = styled.div`
    padding: 16px;
    width: 100%;
    display: block;
    justify-content: flex-start;
`;

export default FallbackMessageForm;